// <purpose>Pure meta helpers turning the D3 reply engine's labels-only envelope
// (SuggestionInfo.replyEngine) into clerk-facing German: zone chip meta (GRÜN/GELB/ROT),
// the no-draft explanation (headline + sentence), and closed-vocab label maps. Single
// source for the isNoDraft predicate consumed by the render branch AND the action guards
// (Kopieren/Genehmigen/Download). Also THE home of the override-id → German map: the ids are
// the shared core MailOverridePipeline's, and both the VNB collision line and the wim triage
// signal badge (overrideSignalBadge) read it — one fact, one home.</purpose>
// <dependencies>theme.ts zone tokens; types.ts ReplyEngineInfo/SuggestionInfo.</dependencies>
// <constraints>Zones are exactly GRÜN/GELB/ROT — suppression is a reason, never a fourth
// zone. Fail-open everywhere: unknown zones/codes render raw rather than throwing, because
// the envelope's vocabulary may grow ahead of this map (e.g. overrideReason). All German
// strings reviewed 2026-07-20 (register: sachlich-knapp, locked); vocabulary must stay
// aligned with the re-scoring brief's zone legend.</constraints>
import { theme } from '../theme';
import { ReplyEngineInfo, SuggestionInfo } from '../types';

export interface ZoneMeta {
  label: string;
  fg: string;
  bg: string;
  border: string;
  legend: string;
}

const ZONES: Record<string, ZoneMeta> = {
  Gruen: { ...theme.colors.zone.gruen, label: 'GRÜN', legend: 'Kein Handlungsbedarf — Versand/Ablage freigegeben' },
  Gelb: { ...theme.colors.zone.gelb, label: 'GELB', legend: 'Bitte prüfen' },
  Rot: { ...theme.colors.zone.rot, label: 'ROT', legend: 'Menschliche Bearbeitung erforderlich' },
};

// The widest doctrine label — ZoneChip's invisible placeholder uses this text (never a guess)
// so the pre-allocated box is at least as wide as any real zone chip that might replace it.
export function widestZoneLabel(): string {
  return Object.values(ZONES).reduce((w, z) => (z.label.length > w.length ? z.label : w), '');
}

export function zoneMeta(zone: string | undefined | null): ZoneMeta {
  if (zone && ZONES[zone]) return ZONES[zone];
  // Fail-open: an unknown zone renders raw and neutral instead of hiding or crashing.
  return {
    label: zone ?? '—',
    fg: theme.colors.navyMid,
    bg: theme.colors.bgLight,
    border: theme.colors.borderLight,
    legend: '',
  };
}

// A deliberate engine no-draft: envelope present AND the displayed text (edit wins) is blank.
export function isNoDraft(suggestion: SuggestionInfo | null | undefined): boolean {
  if (!suggestion?.replyEngine) return false;
  const displayed = suggestion.editedText ?? suggestion.suggestionText;
  return !displayed || displayed.trim().length === 0;
}

export interface NoDraftExplanation {
  headline: string;
  sentence: string;
}

const HEADLINE_DELIBERATE = 'Kein automatischer Entwurf — beabsichtigt';
const HEADLINE_DEGRADED = 'Kein automatischer Entwurf — Analyse eingeschränkt';
const HEADLINE_PLAIN = 'Kein automatischer Entwurf';

// Precedence: override verdicts (wholesale, most specific) → per-request residue reasons →
// degraded-analysis review reasons → plain default. Mirrors the engine's own decision order.
export function noDraftExplanation(
  engine: ReplyEngineInfo,
  reviewReasons: string[] | undefined
): NoDraftExplanation {
  const reasons = reviewReasons ?? [];
  switch (engine.overrideId) {
    case 'abuse':
      return { headline: HEADLINE_DELIBERATE, sentence: 'Sicherheitsvorfall erkannt (Missbrauch/Manipulationsversuch) — keine automatische Antwort.' };
    case 'gdpr_dsr':
      return { headline: HEADLINE_DELIBERATE, sentence: 'DSGVO-Auskunftsersuchen — Weiterleitung an den Datenschutz erforderlich.' };
    case 'legal':
      return { headline: HEADLINE_DELIBERATE, sentence: 'Rechtliche Eskalation — menschliche Bearbeitung erforderlich.' };
    case 'suppress':
      // D1: the envelope's overrideReason (when present) names the suppress kind precisely;
      // null (structural DSN / bulk-silent) falls back to the send-state-based generic copy.
      switch (engine.overrideReason) {
        case 'SUPPRESSED_BULK_OR_JUNK':
          return { headline: HEADLINE_DELIBERATE, sentence: 'Vermutlich Werbung/Massenmail — bitte prüfen.' };
        case 'SUPPRESSED_EMPTY_MAIL':
          return { headline: HEADLINE_DELIBERATE, sentence: 'Leere Nachricht ohne Anliegen — bitte prüfen.' };
        case 'DSN_UNVERIFIED':
          return { headline: HEADLINE_DELIBERATE, sentence: 'Unbestätigter Zustellbericht — bitte prüfen.' };
      }
      return reasons.includes('suppressed_no_reply')
        ? { headline: HEADLINE_DELIBERATE, sentence: 'Automatisch unterdrückt — kein Versand erforderlich.' }
        : { headline: HEADLINE_DELIBERATE, sentence: 'Vermutlich Werbung / automatische Mail — bitte prüfen.' };
  }
  const issueReasons = (engine.requests ?? []).flatMap(r => (r.issues ?? []).map(i => i.reason));
  if (issueReasons.includes('UNSUPPORTED_ACT'))
    return { headline: HEADLINE_PLAIN, sentence: 'Anliegen erkannt — für dieses Thema gibt es noch keine automatische Antwort.' };
  if (issueReasons.includes('AUTHORIZATION_REQUIRED'))
    return { headline: HEADLINE_PLAIN, sentence: 'Berechtigungsprüfung erforderlich — keine automatische Auskunft.' };
  if (reasons.includes('content_filter_fallback') || reasons.includes('fallback'))
    return { headline: HEADLINE_DEGRADED, sentence: 'Automatische Analyse nicht möglich — manuelle Prüfung erforderlich.' };
  return { headline: HEADLINE_PLAIN, sentence: 'Kein automatischer Entwurf — manuelle Bearbeitung.' };
}

// Closed EscalationReasons vocabulary (smarttix.core ReplyContracts.cs) → short clerk German.
// Unknown codes fail open to the raw code — a new upstream reason is visible, never hidden.
const ESCALATION_REASON_LABELS: Record<string, string> = {
  MISSING_BACKEND_STATE: 'Systemdaten nicht verfügbar',
  AUTHORIZATION_REQUIRED: 'Berechtigungsprüfung erforderlich',
  UNSUPPORTED_KB_SUBTOPIC: 'Thema ohne Wissensbasis-Abdeckung',
  UNSUPPORTED_ACT: 'Noch keine Wissensbasis-Antwort',
  AMBIGUOUS_NUMERIC_NOTATION: 'Zahlenangabe mehrdeutig',
  CONFLICTING_CLAIMS: 'Widersprüchliche Angaben',
  MUTUALLY_EXCLUSIVE_ACTIONS: 'Unvereinbare Vorgänge',
  INTERNAL_CLARIFICATION_REQUIRED: 'Interne Klärung erforderlich',
  GDPR_DSR: 'DSGVO-Anfrage',
  LEGAL_ESCALATION: 'Rechtliche Eskalation',
  EMERGENCY_OVERRIDE: 'Gefahrenmeldung',
  ABUSE_OR_INJECTION: 'Sicherheitsvorfall',
  HANDLER_FAILURE: 'Verarbeitungsfehler in Teilschritt',
  TEMPLATE_MISSING: 'Textbaustein fehlt',
  PROCESSING_BUDGET_EXCEEDED: 'Anfragelimit erreicht',
  INCONSISTENT_SEMANTIC_PLAN: 'Antwortplan inkonsistent',
  UNGROUNDED_FUTURE_COMMITMENT: 'Zusage nicht belegbar',
  SLOT_ATTRIBUTION_FAILED: 'Angabe nicht eindeutig zuordenbar',
  DSN_UNVERIFIED: 'Unbestätigter Zustellbericht',
  SUPPRESSED_BULK_OR_JUNK: 'Werbung/Massenmail',
  SUPPRESSED_EMPTY_MAIL: 'Leere Nachricht',
};

export function escalationReasonLabel(code: string): string {
  return ESCALATION_REASON_LABELS[code] ?? code;
}

// Override ids → German nouns naming WHAT WAS DETECTED — for the collision line („Außerdem
// erkannt: …") and the wim triage signal badge alike. Code-owned like DEFAULT_ABBR: override ids
// are product concepts, not tenant vocabulary, so they never ride served data.
const OVERRIDE_NOUNS: Record<string, string> = {
  danger: 'Gefahrenmeldung',
  abuse: 'Sicherheitsvorfall',
  gdpr_dsr: 'DSGVO-Anfrage',
  // "Rechtsbezug", deliberately not "Rechtliche Eskalation" (role-vocabulary round §5): the rule
  // detects that the mail touches legal matter — it does not claim a legal review happened or that
  // the sender is threatening us, and on the wim arm it no longer escalates anything. The no-draft
  // SENTENCE above keeps its escalation wording: there the VNB engine DID escalate.
  legal: 'Rechtsbezug',
  suppress: 'Automatische Mail',
  // Audit-only collision entries the pipeline can add alongside real override ids:
  danger_bounce_only: 'Gefahrenhinweis in zitierter Mail',
  segmentation_uncertain: 'Unsichere Mail-Segmentierung',
};

export function overrideNoun(id: string): string {
  return OVERRIDE_NOUNS[id] ?? id;
}

// The wim triage override SIGNAL badge (role-vocabulary round §5). Since that round a fired
// override at the wim call site is a recorded signal beside a REAL classification — the
// classifier always runs; the override only withholds the automatic acknowledgment. The face is
// the detection noun; the hover names the mechanism honestly (what fired, that classification
// ran, what the rule still withholds). Fail-open on an unknown id: the raw id shows, never nothing.
// `classificationRan` defaults true — the post-split contract. A LEGACY record written before the
// split (decisionSource "override": the classifier was skipped and H4 forced) must not claim
// otherwise; the detail panel passes false for those until reanalysis rewrites them, and the hover
// says so. The list pill never sees a legacy record (core projects no signal for them).
export interface OverrideSignalBadge { face: string; hover: string; }
export const LEGACY_OVERRIDE_DECISION_SOURCE = 'override';
export function overrideSignalBadge(id: string, classificationRan: boolean = true): OverrideSignalBadge {
  const face = overrideNoun(id);
  return {
    face,
    hover: classificationRan
      ? `Regelentscheidung — ${face} erkannt. Die Klassifikation wurde regulär ausgeführt; eine automatische Eingangsbestätigung ist ausgeschlossen.`
      : `Regelentscheidung (Altbestand) — ${face} erkannt. Die Klassifikation wurde bei diesem Vorgang nicht ausgeführt; eine Neuanalyse holt sie nach.`,
  };
}
